<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-row gap-2 items-center justify-between">
      <div class="flex flex-row gap-2 text-base text-semibold">
        <ThemeImage
          :icon="imageNames.RECOMMENDED_PROSPECTS"
          alt="overview_icon"
          class="size-5"
        />

        <div class="flex flex-col gap-1">
          <span class="text-base font-semibold leading-[17px] text_color">
            Recommended Leads
            <span class="text-sm secondary_color font-normal leading-[17px]"
              >(Refreshes daily)</span
            >
          </span>
          <span class="text_color text-[13px] leading-[15px]"
            >New high value leads matching your ICP</span
          >
        </div>
      </div>
    </div>
    <div id="home_recommended_prompts_data" class="proxima_nova">
      <CustomContainer
        :noBackgroundColor="true"
        v-if="loading || !prompts.length"
        class="p-6"
      >
        <SkeletonLoader v-if="loading" :page="loaderPages.TABLE" />
        <EmptyScreen
          v-else
          :text="emptyScreenProps.text"
          source="home_tasks"
          type="prospect"
          :description="emptyScreenProps.description"
        >
          <a-button
            v-if="emptyScreenProps.showCta"
            @click="navigateToIcpSettings"
            class="secondary_medium mt-4"
          >
            Setup ICP
          </a-button>
        </EmptyScreen>
      </CustomContainer>

      <div v-else class="w-full">
        <div
          v-if="numberOfSelected > 0"
          class="flex flex-row border_bottom items-center p-2 h-10 gap-2"
        >
          <SelectionDropdown
            :number-of-selected="numberOfSelected"
            :current-page-data="prompts"
            :all-data="allProspects"
            :total-count="allProspects.length"
            trigger-text="Select prospects"
            :show-unselect-button="false"
            @select-this-page="handleSelectThisPage"
            @select-all="handleSelectAll"
            @select-certain="handleSelectCertain"
            @unselect-all="handleUnselectAll"
          />
          <BulkProspectActionCTA
            :source="entityTypes.LEAD"
            :number-of-selected="numberOfSelected"
            :action-button-text="actionButtonText"
            :entity-permission="entityPermission"
            :is-sequence-enabled="isSequenceEnabled"
            :show-delete="true"
            @trigger-add="handleAction"
            @save-to-list="handleSaveToList"
            @add-to-sequence="handleAddToSequence"
            @delete="handleDelete"
          />
        </div>

        <div class="overflow-x-auto">
          <CustomTable
            :headers="recommendedProspectHeaders"
            :data="prompts"
            :pagination="shouldShowPagination ? pagination : null"
            :add-side-border="true"
            :show-checkboxes="true"
            :custom-column-render="recommendedCustomColumnRender"
            :show-page-size="false"
            @update:pagination="handlePageChange"
            :selection="selection"
            :source="entityTypes.RECOMMENDED_PROMPTS"
            @update:selection="handleSelectionUpdate"
          />
        </div>
      </div>
    </div>

    <SetupSenderDetailsModal
      :pending-validation-data="
        sequenceAddition.state.pendingValidationData || null
      "
      :visible="sequenceAddition.state.modalStates.setupSenderDetailsModal"
      :sequence-id="sequenceId"
      @save="handleSenderSetup"
      @update:visible="
        (visible) =>
          sequenceAddition.setModalState('setupSenderDetailsModal', visible)
      "
      @close="sequenceAddition.handleSetupModalClose"
    />

    <ConfirmationModal
      :visible="deleteConfirmation.visible"
      title="Remove Lead"
      :message="deleteModalMessage"
      :loading="deleteConfirmation.loading"
      type="delete"
      confirm-text="Remove"
      cancel-text="Cancel"
      loading-text="Removing..."
      @update:visible="(val) => (deleteConfirmation.visible = val)"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  recommendedCustomColumnRender,
  recommendedProspectHeaders,
  RECOMMENDED_PROSPECTS_STATUS_CODES,
} from "@/src/constants/homeConstants.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import BulkProspectActionCTA from "@/src/components/BulkProspectActionCTA.vue";
import SelectionDropdown from "@/src/components/SelectionDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import DashboardService from "@/src/api/DashboardService.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import { showSuccessMessage } from "@/src/utils/utils.js";
import { handleRateLimitError } from "@/src/utils/errorUtils.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import CustomContainer from "@/src/components/CustomContainer.vue";
import { useSequenceAddition } from "@/src/composables/useSequenceAddition.js";
import SetupSenderDetailsModal from "@/src/components/sequences_components/SetupSenderDetailsModal.vue";
import { featureNames } from "@/src/constants/globalConstants.js";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";

const router = useRouter();
const prompts = ref([]);
const allProspects = ref([]);
const loading = ref(false);
const pagination = ref({
  current: 1,
  pageSize: 5,
  total: 0,
  showSizeChanger: false,
  pageSizeOptions: ["5"],
});

const selection = ref({
  selected_ids: [],
  unselected_ids: [],
  select_all: false,
});

const numberOfSelected = ref(0);
const isSequenceEnabled = ref(true);
const listId = ref(null);
const sequenceId = ref(null);
const listName = ref(null);
const apiStatusCode = ref(null);
const apiMessage = ref(null);

const sequenceAddition = useSequenceAddition();

const deleteConfirmation = ref({
  visible: false,
  loading: false,
  ids: [],
  isBulkDelete: false,
});

const deleteModalMessage = computed(() => {
  const count = deleteConfirmation.value.ids.length;
  return `Are you sure you want to remove ${count} lead${count > 1 ? "s" : ""} from recommendations?`;
});

const handleSenderSetup = async (setupData) => {
  try {
    sequenceAddition.setModalState("setupSenderDetailsModal", false);
    await toAddLeadsFromFind(setupData.configId);
  } catch (error) {
    console.error("Error in sender setup:", error);
    message.error("Failed to setup sender details. Please try again.");
  }
};

const showSenderDetailsModal = () => {
  if (!sequenceId.value) {
    return;
  }

  sequenceAddition.resetState();
  sequenceAddition.setDirectValidationData(numberOfSelected.value, "DIRECT");
  sequenceAddition.setModalState("setupSenderDetailsModal", true);
};

const hasLeadAccess = computed(() => store.getters["rbac/hasLeadsAccess"]);
const entityPermission = computed(() => hasLeadAccess.value);
const actionButtonText = computed(() => "Save to Leads");
const shouldShowPagination = computed(
  () => allProspects.value.length > pagination.value.pageSize,
);

const emptyScreenProps = computed(() => {
  const statusCodes = Object.values(RECOMMENDED_PROSPECTS_STATUS_CODES);

  if (apiStatusCode.value && statusCodes.includes(apiStatusCode.value)) {
    return {
      text: "",
      description: apiMessage.value,
      showCta:
        apiStatusCode.value ===
        RECOMMENDED_PROSPECTS_STATUS_CODES.NO_LEADS_AND_ICP,
    };
  }

  return {
    text: "No leads found yet!",
    description: "",
    showCta: false,
  };
});

const handleSelectionUpdate = (newSelection) => {
  selection.value = newSelection;
  numberOfSelected.value = newSelection.selected_ids.length;
};

const handlePageChange = (newPagination) => {
  pagination.value = newPagination;
  updateCurrentPageData();
};

const handleSelectThisPage = (currentPageIds) => {
  const newSelectedIds = new Set([
    ...selection.value.selected_ids,
    ...currentPageIds,
  ]);

  selection.value = {
    ...selection.value,
    selected_ids: Array.from(newSelectedIds),
  };
  numberOfSelected.value = selection.value.selected_ids.length;
};

const handleSelectAll = (allIds) => {
  selection.value = {
    selected_ids: allIds,
    unselected_ids: [],
    select_all: false,
  };
  numberOfSelected.value = selection.value.selected_ids.length;
};

const handleSelectCertain = ({ number, selectedIds }) => {
  selection.value = {
    selected_ids: selectedIds,
    unselected_ids: [],
    select_all: false,
  };
  numberOfSelected.value = selectedIds.length;
};

const handleUnselectAll = () => {
  selection.value = {
    selected_ids: [],
    unselected_ids: [],
    select_all: false,
  };
  numberOfSelected.value = 0;
};

const resetSelection = () => {
  selection.value = {
    selected_ids: [],
    unselected_ids: [],
    select_all: false,
  };
  numberOfSelected.value = 0;
  listId.value = null;
  sequenceId.value = null;
  listName.value = null;
};

const handleAction = () => {
  toAddLeadsFromFind();
};

const handleSaveToList = ({ listId: newListId, listName: newListName }) => {
  listId.value = newListId;
  listName.value = newListName;
  toAddLeadsFromFind();
};

const handleAddToSequence = ({ sequenceId: newSequenceId }) => {
  sequenceId.value = newSequenceId;
  showSenderDetailsModal();
};

const handleDelete = () => {
  try {
    const selectedIds = selection.value.selected_ids;
    
    if (!selectedIds || selectedIds.length === 0) {
      message.warning("No leads selected for deletion");
      return;
    }

    deleteConfirmation.value = {
      visible: true,
      loading: false,
      ids: [...selectedIds],
      isBulkDelete: true,
    };
  } catch (error) {
    console.error("Error initiating delete:", error);
    message.error("Failed to initiate delete. Please try again.");
  }
};

const toAddLeadsFromFind = async (sequence_entity_config_id = null) => {
  try {
    const payload = {
      target: numberOfSelected.value,
      selected_ids: selection.value.selected_ids,
      unselected_ids: selection.value.unselected_ids,
      filters: {},
      list_id: listId.value,
      sequence_id: sequenceId.value,
      enrich_phone: false,
      source: "Recommended Leads",
    };

    if (sequence_entity_config_id) {
      payload.sequence_entity_config_id = sequence_entity_config_id;
    }

    const response = await DashboardService.addLeadsFromFind(payload);
    if (response?.success) {
      if (sequenceId.value) {
        message.success(
          `Leads added to ${featureNames.SEQUENCES} successfully`,
        );
      } else if (listId.value) {
        const list = { id: listId.value, name: listName.value };
        await showSuccessMessage(
          router,
          numberOfSelected.value,
          entityTypes.LEAD,
          list,
        );
      } else {
        await showSuccessMessage(
          router,
          numberOfSelected.value,
          entityTypes.LEAD,
        );
      }

      resetSelection();
    } else {
      message.error(response?.message || "Failed to process leads");
    }
  } catch (error) {
    if (handleRateLimitError(error, message)) {
      return;
    }
    console.error("An error occurred while processing leads: ", error);
  }
};

const updateCurrentPageData = () => {
  if (allProspects.value.length === 0) {
    prompts.value = [];
    return;
  }

  const startIndex = (pagination.value.current - 1) * pagination.value.pageSize;
  const endIndex = startIndex + pagination.value.pageSize;
  prompts.value = allProspects.value.slice(startIndex, endIndex);
};

const toFetchRecommendPrompts = async () => {
  loading.value = true;

  try {
    const response = await DashboardService.fetchProspectRecommendations({});

    if (response?.success) {
      apiStatusCode.value = response?.status_code;
      apiMessage.value = response?.message;

      allProspects.value = response?.results?.people || [];
      pagination.value.total = allProspects.value.length;
      updateCurrentPageData();
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching recommend prospect headers: ",
      error,
    );
  } finally {
    loading.value = false;
  }
};

const handleTableRowUpdate = (updatedData) => {
  const allRowIndex = allProspects.value.findIndex(
    (item) => item.id === updatedData?.id,
  );
  if (allRowIndex === -1) return;

  const updatedRow = {
    ...allProspects.value[allRowIndex],
    lead_id:
      updatedData?.lead?.id ||
      allProspects.value[allRowIndex]?.lead_id ||
      updatedData?.lead?.id ||
      updatedData?.id,
  };

  if (
    updatedData?.action === "email" &&
    (updatedData?.lead?.email || updatedData?.email)
  ) {
    updatedRow.email = updatedData?.lead?.email || updatedData?.email;
  }

  if (
    updatedData?.action === "call" &&
    (updatedData?.lead?.phone_number || updatedData?.phone_number)
  ) {
    updatedRow.phone_number =
      updatedData?.lead?.phone_number || updatedData?.phone_number;
  }

  const newAllData = [...allProspects.value];
  newAllData[allRowIndex] = updatedRow;
  allProspects.value = newAllData;
  updateCurrentPageData();
};

const navigateToIcpSettings = () => {
  router.push("/settings#business");
};

const handleSingleDelete = ({ id }) => {
  if (!id) {
    message.warning("No lead selected for deletion");
    return;
  }

  deleteConfirmation.value = {
    visible: true,
    loading: false,
    ids: [id],
    isBulkDelete: false,
  };
};

const handleDeleteConfirm = async () => {
  const idsToDelete = deleteConfirmation.value.ids;
  const isBulkDelete = deleteConfirmation.value.isBulkDelete;
  
  deleteConfirmation.value.loading = true;

  try {
    const payload = { ids: idsToDelete };
    const response = await DashboardService.bulkDeleteRecommendedLeads(payload);
    
    if (response?.success) {
      if (isBulkDelete) {
        resetSelection();
      }
      const successMsg = isBulkDelete 
        ? "Selected leads removed from recommendations" 
        : "Lead removed from recommendations";
      message.success(successMsg);
      deleteConfirmation.value.visible = false;
      await toFetchRecommendPrompts();
    } else {
      const errorMsg = isBulkDelete 
        ? "Failed to delete selected leads" 
        : "Failed to delete the lead";
      message.error(response?.message || errorMsg);
    }
  } catch (error) {
    console.error("Error deleting recommended leads:", error);
    const errorMsg = isBulkDelete 
      ? "Failed to delete selected leads. Please try again." 
      : "Failed to delete the lead. Please try again.";
    message.error(errorMsg);
  } finally {
    deleteConfirmation.value.loading = false;
  }
};

const handleDeleteCancel = () => {
  deleteConfirmation.value = {
    visible: false,
    loading: false,
    ids: [],
    isBulkDelete: false,
  };
};

onMounted(() => {
  toFetchRecommendPrompts();
  isSequenceEnabled.value = localStorage.getItem("sequence") || false;
  eventBus.on("table-row-updated", handleTableRowUpdate);
  eventBus.on("delete-recommended-lead", handleSingleDelete);
});

onUnmounted(() => {
  eventBus.off("table-row-updated", handleTableRowUpdate);
  eventBus.off("delete-recommended-lead", handleSingleDelete);
});
</script>

